import React from 'react'
import { Button, Card, Collapse, Input, Select } from 'antd'
import PropTypes from 'prop-types'

const { Panel } = Collapse
const TARGET_OPTIONS = [
  { label: 'SELF', value: '_self' },
  { label: 'BLANK', value: '_blank' }
]
const SOCIAL_NAME_OPTIONS = [
  { label: 'facebook', value: 'facebook' },
  { label: 'twitter', value: 'twitter' },
  { label: 'instagram', value: 'instagram' },
  { label: 'youtube', value: 'youtube' },
  { label: 'snapchat', value: 'snapchat' },
  { label: 'linkedin', value: 'linkedin' }
]

const emptyLinkItem = () => ({ label: '', url: '', target: '_self' })
const emptyLinkSection = () => ({ headline: '', link_items: [] })
const emptyBottomBandItem = () => ({ label: '', url: '', target: '_self' })
const emptySocialLink = () => ({ name: '', url: '', target: '_self' })
const getCollapseHeader = label => <span style={{ cursor: 'pointer' }}>{label}</span>

export const EMPTY_FOOTER_PAYLOAD = {
  app: 'MSG',
  links: [],
  bottom_band_items: [],
  copyright_label: '',
  company_information_section: {
    eyebrow: '',
    information_text: ''
  },
  social_header: '',
  social_links: [],
  email_capture_form: {
    description: '',
    disclaimer: '',
    error_message: '',
    formstack_id: '',
    success_message: ''
  }
}

const hasText = value => typeof value === 'string' && value.trim().length > 0

export const getFooterValidationErrors = footer => {
  const payload = footer || {}
  const errors = []

  if (!hasText(payload.app)) errors.push('App Name is required.')
  if (!hasText(payload.social_header)) errors.push('Social Header is required.')
  if (!hasText(payload.copyright_label)) errors.push('Copyright Label is required.')

  const companyInfo = payload.company_information_section || {}
  if (!hasText(companyInfo.eyebrow)) errors.push('Company Eyebrow is required.')
  if (!hasText(companyInfo.information_text)) errors.push('Company Phone / Information Text is required.')

  const emailCapture = payload.email_capture_form || {}
  if (!hasText(emailCapture.description)) errors.push('Email Capture Form Description is required.')
  if (!hasText(emailCapture.disclaimer)) errors.push('Email Capture Form Disclaimer is required.')
  if (!hasText(emailCapture.error_message)) errors.push('Email Capture Form Error Message is required.')
  if (!hasText(emailCapture.formstack_id)) errors.push('Email Capture Form Formstack ID is required.')
  if (!hasText(emailCapture.success_message)) errors.push('Email Capture Form Success Message is required.')

  const socialLinks = Array.isArray(payload.social_links) ? payload.social_links : []
  if (socialLinks.length === 0) {
    errors.push('At least one Social Link is required.')
  } else {
    socialLinks.forEach((item, index) => {
      if (!hasText(item?.name)) errors.push(`Social Link ${index + 1}: Name is required.`)
      if (!hasText(item?.url)) errors.push(`Social Link ${index + 1}: URL is required.`)
      if (!hasText(item?.target)) errors.push(`Social Link ${index + 1}: Target is required.`)
    })
  }

  const links = Array.isArray(payload.links) ? payload.links : []
  if (links.length === 0) {
    errors.push('At least one Link column is required.')
  } else {
    links.forEach((section, sectionIndex) => {
      if (!hasText(section?.headline)) {
        errors.push(`Link column ${sectionIndex + 1}: Headline is required.`)
      }
      const linkItems = Array.isArray(section?.link_items) ? section.link_items : []
      if (linkItems.length === 0) {
        errors.push(`Link column ${sectionIndex + 1}: At least one Link Item is required.`)
      } else {
        linkItems.forEach((item, itemIndex) => {
          if (!hasText(item?.label)) errors.push(`Link column ${sectionIndex + 1}, item ${itemIndex + 1}: Label is required.`)
          if (!hasText(item?.url)) errors.push(`Link column ${sectionIndex + 1}, item ${itemIndex + 1}: URL is required.`)
          if (!hasText(item?.target)) errors.push(`Link column ${sectionIndex + 1}, item ${itemIndex + 1}: Target is required.`)
        })
      }
    })
  }

  const bottomBandItems = Array.isArray(payload.bottom_band_items) ? payload.bottom_band_items : []
  if (bottomBandItems.length === 0) {
    errors.push('At least one Bottom Band Item is required.')
  } else {
    bottomBandItems.forEach((item, index) => {
      if (!hasText(item?.label)) errors.push(`Bottom Band Item ${index + 1}: Label is required.`)
      if (!hasText(item?.url)) errors.push(`Bottom Band Item ${index + 1}: URL is required.`)
      if (!hasText(item?.target)) errors.push(`Bottom Band Item ${index + 1}: Target is required.`)
    })
  }

  return errors
}

const FooterForm = ({ footer, setFooter }) => {
  const updateTopLevel = (field, value) => {
    setFooter(prev => ({
      ...prev,
      [field]: value
    }))
  }

  const updateCompanyInfo = (field, value) => {
    setFooter(prev => ({
      ...prev,
      company_information_section: {
        ...(prev.company_information_section || {}),
        [field]: value
      }
    }))
  }

  const updateEmailCapture = (field, value) => {
    setFooter(prev => ({
      ...prev,
      email_capture_form: {
        ...(prev.email_capture_form || {}),
        [field]: value
      }
    }))
  }

  const addBottomBandItem = () => {
    setFooter(prev => ({
      ...prev,
      bottom_band_items: [...(prev.bottom_band_items || []), emptyBottomBandItem()]
    }))
  }

  const updateBottomBandItem = (index, field, value) => {
    setFooter(prev => ({
      ...prev,
      bottom_band_items: (prev.bottom_band_items || []).map((item, idx) => {
        if (idx !== index) return item
        return {
          ...item,
          [field]: value
        }
      })
    }))
  }

  const removeBottomBandItem = index => {
    setFooter(prev => ({
      ...prev,
      bottom_band_items: (prev.bottom_band_items || []).filter((_, idx) => idx !== index)
    }))
  }

  const addSocialLink = () => {
    setFooter(prev => ({
      ...prev,
      social_links: [...(prev.social_links || []), emptySocialLink()]
    }))
  }

  const updateSocialLink = (index, field, value) => {
    setFooter(prev => ({
      ...prev,
      social_links: (prev.social_links || []).map((item, idx) => {
        if (idx !== index) return item
        return {
          ...item,
          [field]: value
        }
      })
    }))
  }

  const removeSocialLink = index => {
    setFooter(prev => ({
      ...prev,
      social_links: (prev.social_links || []).filter((_, idx) => idx !== index)
    }))
  }

  const addLinkSection = () => {
    setFooter(prev => ({
      ...prev,
      links: [...(prev.links || []), emptyLinkSection()]
    }))
  }

  const removeLinkSection = sectionIndex => {
    setFooter(prev => ({
      ...prev,
      links: (prev.links || []).filter((_, idx) => idx !== sectionIndex)
    }))
  }

  const updateLinkSectionHeadline = (sectionIndex, headline) => {
    setFooter(prev => ({
      ...prev,
      links: (prev.links || []).map((section, idx) => {
        if (idx !== sectionIndex) return section
        return {
          ...section,
          headline
        }
      })
    }))
  }

  const addLinkItem = sectionIndex => {
    setFooter(prev => ({
      ...prev,
      links: (prev.links || []).map((section, idx) => {
        if (idx !== sectionIndex) return section
        return {
          ...section,
          link_items: [...(section.link_items || []), emptyLinkItem()]
        }
      })
    }))
  }

  const removeLinkItem = (sectionIndex, linkIndex) => {
    setFooter(prev => ({
      ...prev,
      links: (prev.links || []).map((section, idx) => {
        if (idx !== sectionIndex) return section
        return {
          ...section,
          link_items: (section.link_items || []).filter((_, itemIdx) => itemIdx !== linkIndex)
        }
      })
    }))
  }

  const updateLinkItem = (sectionIndex, linkIndex, field, value) => {
    setFooter(prev => ({
      ...prev,
      links: (prev.links || []).map((section, idx) => {
        if (idx !== sectionIndex) return section
        return {
          ...section,
          link_items: (section.link_items || []).map((item, itemIdx) => {
            if (itemIdx !== linkIndex) return item
            return {
              ...item,
              [field]: value
            }
          })
        }
      })
    }))
  }

  return (
    <Card className='footer-form'>
      <style>
        {`
          .footer-form .ant-collapse-header,
          .footer-form .ant-collapse-arrow {
            cursor: pointer !important;
          }
        `}
      </style>
      <div style={{ marginBottom: 16 }}>
        <div style={{ marginBottom: 12 }}>
          <label>App Name</label>
          <Input onChange={event => updateTopLevel('app', event.target.value)} value={footer.app || ''} />
        </div>
        <div style={{ marginTop: 16 }}>
          <Collapse>
            <Panel key='social-footer' header={getCollapseHeader('Social footer')}>
              <div style={{ marginBottom: 12 }}>
                <label>Social Header</label>
                <Input
                  onChange={event => updateTopLevel('social_header', event.target.value)}
                  value={footer.social_header || ''}
                />
              </div>
              <div style={{ marginBottom: 8 }}>
                <label>Social Links</label>
              </div>
              <div style={{ marginBottom: 12 }}>
                <Button onClick={addSocialLink} type='primary'>Add Social Link</Button>
              </div>
              <Collapse>
                {(footer.social_links || []).map((item, index) => (
                  <Panel
                    key={`social-link-${index}`}
                    header={getCollapseHeader(item.name || `Social Link ${index + 1}`)}
                  >
                    <div style={{ marginBottom: 12 }}>
                      <label>Name</label>
                      <Select
                        onChange={value => updateSocialLink(index, 'name', value)}
                        options={SOCIAL_NAME_OPTIONS}
                        style={{ width: 220 }}
                        value={item.name || undefined}
                      />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label>URL</label>
                      <Input onChange={event => updateSocialLink(index, 'url', event.target.value)} value={item.url || ''} />
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <label>Target</label>
                      <Select
                        onChange={value => updateSocialLink(index, 'target', value)}
                        options={TARGET_OPTIONS}
                        style={{ width: 180 }}
                        value={item.target || '_self'}
                      />
                    </div>
                    <Button danger onClick={() => removeSocialLink(index)}>
                    Delete Social Link
                    </Button>
                  </Panel>
                ))}
              </Collapse>
            </Panel>
          </Collapse>
        </div>
        <div style={{ marginTop: 16 }}>
          <Collapse>
            <Panel key='email-capture-form' header={getCollapseHeader('Email Capture Form')}>
              <div style={{ marginBottom: 12 }}>
                <label>Description</label>
                <Input.TextArea
                  autoSize={{ minRows: 3 }}
                  onChange={event => updateEmailCapture('description', event.target.value)}
                  value={footer.email_capture_form?.description || ''}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>Disclaimer</label>
                <Input.TextArea
                  autoSize={{ minRows: 3 }}
                  onChange={event => updateEmailCapture('disclaimer', event.target.value)}
                  value={footer.email_capture_form?.disclaimer || ''}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>Error Message</label>
                <Input
                  onChange={event => updateEmailCapture('error_message', event.target.value)}
                  value={footer.email_capture_form?.error_message || ''}
                />
              </div>
              <div style={{ marginBottom: 12 }}>
                <label>Formstack ID</label>
                <Input
                  onChange={event => updateEmailCapture('formstack_id', event.target.value)}
                  value={footer.email_capture_form?.formstack_id || ''}
                />
              </div>
              <div>
                <label>Success Message</label>
                <Input
                  onChange={event => updateEmailCapture('success_message', event.target.value)}
                  value={footer.email_capture_form?.success_message || ''}
                />
              </div>
            </Panel>
          </Collapse>
        </div>
        <div style={{ marginTop: 16 }}>
          <Collapse>
            <Panel key='link-sections' header={getCollapseHeader('Link columns')}>
              <div style={{ marginBottom: 12 }}>
                <Button onClick={addLinkSection} type='primary'>Add Column</Button>
              </div>
              <Collapse>
                {(footer.links || []).map((section, sectionIndex) => (
                  <Panel
                    key={`section-${sectionIndex}`}
                    header={getCollapseHeader(section.headline || `Column ${sectionIndex + 1}`)}
                  >
                    <div style={{ marginBottom: 12 }}>
                      <label>Headline</label>
                      <Input
                        onChange={event => updateLinkSectionHeadline(sectionIndex, event.target.value)}
                        value={section.headline || ''}
                      />
                    </div>
                    <Button onClick={() => addLinkItem(sectionIndex)} type='primary'>Add Link Item</Button>
                    <div style={{ marginTop: 12 }}>
                      {(section.link_items || []).map((item, linkIndex) => (
                        <Card
                          key={`section-${sectionIndex}-item-${linkIndex}`}
                          size='small'
                          style={{ marginBottom: 12 }}
                          title={`Link Item ${linkIndex + 1}`}
                        >
                          <div style={{ marginBottom: 12 }}>
                            <label>Label</label>
                            <Input
                              onChange={event => updateLinkItem(sectionIndex, linkIndex, 'label', event.target.value)}
                              value={item.label || ''}
                            />
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <label>URL</label>
                            <Input
                              onChange={event => updateLinkItem(sectionIndex, linkIndex, 'url', event.target.value)}
                              value={item.url || ''}
                            />
                          </div>
                          <div style={{ marginBottom: 12 }}>
                            <label>Target</label>
                            <Select
                              onChange={value => updateLinkItem(sectionIndex, linkIndex, 'target', value)}
                              options={TARGET_OPTIONS}
                              style={{ width: 180 }}
                              value={item.target || '_self'}
                            />
                          </div>
                          <Button danger onClick={() => removeLinkItem(sectionIndex, linkIndex)}>
                          Delete Link Item
                          </Button>
                        </Card>
                      ))}
                    </div>
                    <Button danger onClick={() => removeLinkSection(sectionIndex)}>Delete Section</Button>
                  </Panel>
                ))}
              </Collapse>
            </Panel>
          </Collapse>
        </div>
        <div style={{ marginTop: 16, marginBottom: 12 }}>
          <label>Company Eyebrow</label>
          <Input
            onChange={event => updateCompanyInfo('eyebrow', event.target.value)}
            value={footer.company_information_section?.eyebrow || ''}
          />
        </div>
        <div>
          <label>Company Phone / Information Text</label>
          <Input
            onChange={event => updateCompanyInfo('information_text', event.target.value)}
            value={footer.company_information_section?.information_text || ''}
          />
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <Collapse>
          <Panel key='bottom-band-items' header={getCollapseHeader('Bottom Band Items')}>
            <div style={{ marginBottom: 12 }}>
              <Button onClick={addBottomBandItem} type='primary'>Add Bottom Band Item</Button>
            </div>
            <Collapse>
              {(footer.bottom_band_items || []).map((item, index) => (
                <Panel
                  key={`bottom-band-item-${index}`}
                  header={getCollapseHeader(item.label || `Bottom Band Item ${index + 1}`)}
                >
                  <div style={{ marginBottom: 12 }}>
                    <label>Label</label>
                    <Input onChange={event => updateBottomBandItem(index, 'label', event.target.value)} value={item.label || ''} />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label>URL</label>
                    <Input onChange={event => updateBottomBandItem(index, 'url', event.target.value)} value={item.url || ''} />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label>Target</label>
                    <Select
                      onChange={value => updateBottomBandItem(index, 'target', value)}
                      options={TARGET_OPTIONS}
                      style={{ width: 180 }}
                      value={item.target || '_self'}
                    />
                  </div>
                  <Button danger onClick={() => removeBottomBandItem(index)}>
                    Delete Bottom Band Item
                  </Button>
                </Panel>
              ))}
            </Collapse>
          </Panel>
        </Collapse>
      </div>
      <div style={{ marginTop: 16 }}>
        <label>Copyright Label</label>
        <Input
          onChange={event => updateTopLevel('copyright_label', event.target.value)}
          value={footer.copyright_label || ''}
        />
      </div>
    </Card>
  )
}

FooterForm.propTypes = {
  footer: PropTypes.shape({
    app: PropTypes.string,
    links: PropTypes.arrayOf(PropTypes.shape({
      headline: PropTypes.string,
      link_items: PropTypes.arrayOf(PropTypes.shape({
        label: PropTypes.string,
        url: PropTypes.string,
        target: PropTypes.string
      }))
    })),
    bottom_band_items: PropTypes.arrayOf(PropTypes.shape({
      label: PropTypes.string,
      url: PropTypes.string,
      target: PropTypes.string
    })),
    copyright_label: PropTypes.string,
    company_information_section: PropTypes.shape({
      eyebrow: PropTypes.string,
      information_text: PropTypes.string
    }),
    social_header: PropTypes.string,
    social_links: PropTypes.arrayOf(PropTypes.shape({
      name: PropTypes.string,
      url: PropTypes.string,
      target: PropTypes.string
    })),
    email_capture_form: PropTypes.shape({
      description: PropTypes.string,
      disclaimer: PropTypes.string,
      error_message: PropTypes.string,
      formstack_id: PropTypes.string,
      success_message: PropTypes.string
    })
  }),
  setFooter: PropTypes.func
}

export default FooterForm
